import type { App, Route } from "@pimo/pimo-app-builder";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { Attachment, BffOe, Initiative, UnsavedAttachment } from "aint-types";
import {
  convertImpactValuesUnit,
  createEmptyInitiative,
  LOCK_RESOURCE_NAMES,
  OES_WITH_DEPENDENT_FIELDS,
  OES_WITH_EXTENDED_LINE_OF_BUSINESS_OPTIONS,
} from "aint-utils";
import { generatePath } from "react-router-dom";

import { InAppState } from "../app/app";
import { APP_ROUTES } from "../app/constants";
import {
  addNewInitiative,
  fetchEditInitiative,
  fetchOE,
  fetchOEInitiatives,
  fetchOEs,
  markInitiativeDeleted,
  saveInitiative,
} from "../app/helpers/fetch-helpers";
import { EditInitiativeDialog } from "../app/pages/edit-initiative-dialog/edit-initiative-dialog";
import {
  removeUnusedFieldsFromEditInitiative,
  roundFteImpact,
} from "../app/utils";
import { INFormConfirmationDialog } from "../components/in-form-confirmation-dialog";
import { UploadDialog } from "../components/upload-dialog/upload-dialog";

export function buildFormRoute(
  app: App<InAppState>,
  parentRoute: Route<"oeId" | "viewname", InAppState>
) {
  let oeId: string, initiativeUid: string;

  const view = app.createView({
    name: "Edit-Initiatives",
  });

  const editDialog = view.addComponent({
    component: EditInitiativeDialog,
  });

  editDialog.mapState(
    ({
      currentOE,
      currentEditInitiative,
      currentSupportingDocuments,
      currentUnsavedSupportingDocuments,
      editFormLoading: isLoading,
      editFormSaving: isSaving,
      userProfile,
    }) => {
      const dependentFields =
        OES_WITH_DEPENDENT_FIELDS[
          currentOE?.operatingEntity as keyof typeof OES_WITH_DEPENDENT_FIELDS
        ] ?? ([] as string[]);
      const showInterOeRegionField = dependentFields.includes("interOeRegion");
      const showBusinessUnitField = dependentFields.includes("businessUnit");
      const showCategoryField = dependentFields.includes("category");
      const showOeProgramFields = dependentFields.includes("oeProgram");
      const showElevateImplProgramOption =
        currentOE?.operatingEntity === "Allianz Partners" ||
        currentOE?.operatingEntity === "Allianz Partners Beta Test";
      const showExtendedLobOptions = currentOE
        ? OES_WITH_EXTENDED_LINE_OF_BUSINESS_OPTIONS.includes(
            currentOE?.operatingEntity
          )
        : false;
      const usersWithAccessToOE = (currentOE?.userEmailsWithAccess ?? []).map(
        ({ email }) => email
      );

      if (userProfile) {
        const userProfileIndex = usersWithAccessToOE.findIndex(
          (email) => email === userProfile.email
        );

        if (userProfileIndex !== -1) {
          // Remove creator from old position and insert at the top.
          usersWithAccessToOE.splice(userProfileIndex, 1);
        }

        usersWithAccessToOE.unshift(userProfile.email);
      }

      return {
        title: currentEditInitiative?.id
          ? (currentEditInitiative?.initiativeName ?? "")
          : "Add Initiative",
        usersWithAccessToOE,
        showInterOeRegionField,
        showBusinessUnitField,
        showCategoryField,
        showOeProgramFields,
        showExtendedLobOptions,
        showElevateImplProgramOption,
        isLoading,
        isSaving,
        initiative: currentEditInitiative,
        supportingDocuments: [
          ...currentSupportingDocuments,
          ...currentUnsavedSupportingDocuments,
        ],
        isCreate: initiativeUid === "new",
      };
    }
  );

  const uploadDocumentDialog = view.addComponent({
    component: UploadDialog,
  });

  uploadDocumentDialog.mapState(({ supportingDocumentsDialogOpen }) => ({
    isOpen: supportingDocumentsDialogOpen,
    fileType: "all",
  }));

  uploadDocumentDialog.on("upload-dialog:close", () => {
    app.patchAppState({ supportingDocumentsDialogOpen: false });
  });

  uploadDocumentDialog.on("upload-dialog:upload-file", async ({ payload }) => {
    if (!payload) {
      return;
    }

    const { file, comment } = payload;

    const { currentUnsavedSupportingDocuments } = app.getAppState();

    app.patchAppState({
      supportingDocumentsDialogOpen: false,
      currentUnsavedSupportingDocuments: [
        ...currentUnsavedSupportingDocuments,
        { file, comment },
      ],
    });

    return;
  });

  const route = parentRoute.createChildRoute({
    path: APP_ROUTES.editInitiatives,
    view: view,
  }) as Route<"oeId" | "viewname" | "initiativeUid">;

  route.on("load", async ({ payload }) => {
    if (!payload?.parameters?.oeId || !payload?.parameters.initiativeUid) {
      return;
    }
    app.patchAppState({ editFormLoading: true });

    initiativeUid = payload.parameters.initiativeUid;
    oeId = payload.parameters.oeId;

    const statePatch: {
      currentEditInitiative?: Initiative;
      currentOE?: BffOe;
      currentSupportingDocuments?: Attachment[];
      currentUnsavedSupportingDocuments?: UnsavedAttachment[];
    } = {};

    if (initiativeUid !== "new") {
      statePatch.currentEditInitiative = convertImpactValuesUnit({
        unit: "k EUR",
        initiative: await fetchEditInitiative(oeId, initiativeUid),
        plannedOrActualArray: ["Planned"],
      });
      statePatch.currentSupportingDocuments =
        statePatch.currentEditInitiative?.supportingDocuments ?? [];
      statePatch.currentUnsavedSupportingDocuments = [];
    } else {
      statePatch.currentEditInitiative =
        createEmptyInitiative() as unknown as Initiative;
      statePatch.currentUnsavedSupportingDocuments = [];
      statePatch.currentSupportingDocuments = [];
    }

    const appState = app.getAppState();

    if (!appState.currentOE) {
      statePatch.currentOE = await fetchOE(oeId);
    }

    app.patchAppState({ ...statePatch, editFormLoading: false });
  });

  registerFormEvents();
  createConfirmationDialog();

  function registerFormEvents() {
    editDialog.on("edit-dialog:submit", async ({ payload }) => {
      app?.patchAppState({
        editFormSaving: true,
      });
      await saveInitiativeOrDraft(payload?.initiative as Initiative);
      app?.patchAppState({
        editFormSaving: false,
        snackbar: {
          message: "All changes have been saved.",
          severity: "success",
          open: true,
          type: "snackbar",
        },
      });
    });

    editDialog.on("edit-dialog:submit-draft", async ({ payload = {} }) => {
      const initiativeToSave = payload.initiative as Initiative;
      app?.patchAppState({
        editFormSaving: true,
      });
      initiativeToSave.draft = true;
      await saveInitiativeOrDraft(initiativeToSave);
      app?.patchAppState({
        editFormSaving: false,
        snackbar: {
          message: "All changes have been saved.",
          severity: "success",
          open: true,
          type: "snackbar",
        },
      });
    });

    editDialog.on("edit-dialog:publish", async ({ payload }) => {
      app.patchAppState({
        showEditFormConfirmationDialog: true,
        editFormConfirmationDialogType: "publish-confirmation",
        draftInitiativeToPublish: payload?.initiative as Initiative,
      });
    });

    editDialog.on("edit-dialog:close-with-changes", () =>
      app.patchAppState({
        showEditFormConfirmationDialog: true,
        editFormConfirmationDialogType: "cancel-with-changes",
      })
    );

    editDialog.on("edit-dialog:close", () => {
      const resourceLockPlugin =
        app.getPluginByName<ResourceLockPlugin<InAppState>>(
          "ResourceLockPlugin"
        );
      const currentEditInitiative = app.getAppState().currentEditInitiative;

      if (!resourceLockPlugin || !currentEditInitiative) {
        return;
      }

      resourceLockPlugin.unlock(
        `${LOCK_RESOURCE_NAMES.initiative}-${currentEditInitiative.id}`
      );

      return app.navigate(
        generatePath(APP_ROUTES.operatingEntities, {
          viewname: "initiatives",
          oeId,
        })
      );
    });

    editDialog.on("edit-dialog:delete", () => {
      app.patchAppState({
        editFormConfirmationDialogType: "delete-confirmation",
        showEditFormConfirmationDialog: true,
      });
    });

    editDialog.on("edit-dialog:report-impact", () => {
      const { currentEditInitiative, currentOE } = app.getAppState();

      if (!currentEditInitiative || !currentOE) {
        return;
      }

      app.navigate(
        generatePath(APP_ROUTES.impactUpdate, {
          viewname: "initiatives",
          oeId: currentOE.id,
          initiativeUid: currentEditInitiative.uid,
        })
      );
    });

    editDialog.on("edit-dialog:report-impact-with-changes", () => {
      const { currentEditInitiative, currentOE } = app.getAppState();

      if (!currentEditInitiative || !currentOE) {
        return;
      }

      app.patchAppState({
        editFormConfirmationDialogType: "navigate-with-changes",
        showEditFormConfirmationDialog: true,
        editFormNavigationPath: generatePath(APP_ROUTES.impactUpdate, {
          viewname: "initiatives",
          oeId: currentOE.id,
          initiativeUid: currentEditInitiative.uid,
        }),
      });
    });

    editDialog.on("edit-dialog:upload-document-button-click", async () => {
      app.patchAppState({ supportingDocumentsDialogOpen: true });
    });
    editDialog.on(
      "edit-dialog:delete-document-button-click",
      async ({ payload }) => {
        const { currentUnsavedSupportingDocuments } = app.getAppState();

        if (payload?.fileId) {
          app.patchAppState({
            editFormConfirmationDialogType: "delete-document-confirmation",
            showEditFormConfirmationDialog: true,
            supportingDocumentIdToDelete: payload.fileId,
          });
        }

        if (payload?.fileIndex !== undefined) {
          const newCreateDocs = [...currentUnsavedSupportingDocuments];
          newCreateDocs.splice(payload.fileIndex, 1);
          app.patchAppState({
            currentUnsavedSupportingDocuments: newCreateDocs,
          });
        }
      }
    );
  }

  async function deleteDocument(fileId?: string, initiativeId?: string) {
    if (!fileId || !initiativeId) {
      return;
    }

    const { currentSupportingDocuments } = app.getAppState();
    const fileIndex = currentSupportingDocuments.findIndex(
      (file) => file.id === fileId
    );

    currentSupportingDocuments.splice(fileIndex, 1);

    app.patchAppState({
      currentSupportingDocuments: currentSupportingDocuments,
    });
  }

  async function deleteInitiative() {
    const state = app.getAppState();
    const { currentOE, currentEditInitiative: initiative } = state;

    if (!initiative?.id) {
      return;
    }

    await markInitiativeDeleted(initiative.id);

    if (currentOE) {
      const currentOEInitiatives = await fetchOEInitiatives(currentOE.id);
      const groupOverview = await fetchOEs(undefined);

      app.patchAppState({
        currentOEInitiatives,
        groupOverview,
      });

      const resourceLockPlugin =
        app.getPluginByName<ResourceLockPlugin<InAppState>>(
          "ResourceLockPlugin"
        );
      resourceLockPlugin?.unlock(
        `${LOCK_RESOURCE_NAMES.oeInitiatives}-${oeId}`
      );

      app.navigate(
        generatePath(APP_ROUTES.operatingEntities, {
          oeId: currentOE.id,
          viewname: "initiatives",
        })
      );
    }
  }

  async function saveInitiativeOrDraft(initiativeToSave: Initiative) {
    if (!initiativeToSave) {
      return;
    }

    removeUnusedFieldsFromEditInitiative(initiativeToSave);
    convertImpactValuesUnit({
      unit: "EUR",
      initiative: initiativeToSave,
      plannedOrActualArray: ["Planned"],
    });
    roundFteImpact(initiativeToSave);

    const { currentSupportingDocuments, currentUnsavedSupportingDocuments } =
      app.getAppState();

    try {
      if (initiativeToSave?.id) {
        await saveInitiative({
          id: initiativeToSave.id,
          initiative: initiativeToSave,
          savedAttachments: currentSupportingDocuments,
          unsavedAttachments: currentUnsavedSupportingDocuments,
        });
      } else {
        await addNewInitiative(
          oeId,
          initiativeToSave,
          app.getAppState().currentUnsavedSupportingDocuments
        );
      }
    } catch {
      return;
    }

    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<InAppState>>("ResourceLockPlugin");
    resourceLockPlugin?.unlock(`${LOCK_RESOURCE_NAMES.oeInitiatives}-${oeId}`);

    const currentEditInitiative = convertImpactValuesUnit({
      unit: "k EUR",
      initiative: await fetchEditInitiative(oeId, initiativeToSave.uid),
      plannedOrActualArray: ["Planned"],
    });

    app.patchAppState({
      currentOEInitiatives: await fetchOEInitiatives(oeId),
      currentEditInitiative,
      currentSupportingDocuments:
        currentEditInitiative?.supportingDocuments ?? [],
      currentUnsavedSupportingDocuments: [],
    });

    if (!initiativeToSave?.id) {
      app.navigate(
        generatePath(APP_ROUTES.operatingEntities, {
          viewname: "initiatives",
          oeId,
        })
      );
    }
  }

  function createConfirmationDialog() {
    const confirmationDialog = view.addComponent({
      component: INFormConfirmationDialog,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog && showEditFormConfirmationDialog != null
    );

    confirmationDialog.mapState(({ editFormConfirmationDialogType }) => ({
      type: editFormConfirmationDialogType,
    }));

    function closeConfirmationDialog() {
      app.patchAppState({
        showEditFormConfirmationDialog: false,
      });
    }

    confirmationDialog.on("confirm", async () => {
      closeConfirmationDialog();

      const resourceLockPlugin =
        app.getPluginByName<ResourceLockPlugin<InAppState>>(
          "ResourceLockPlugin"
        );
      const {
        editFormConfirmationDialogType,
        draftInitiativeToPublish,
        editFormNavigationPath,
        supportingDocumentIdToDelete,
        currentEditInitiative,
      } = app.getAppState();

      switch (editFormConfirmationDialogType) {
        case "cancel-with-changes":
          if (currentEditInitiative) {
            resourceLockPlugin?.unlock(
              `${LOCK_RESOURCE_NAMES.initiative}-${currentEditInitiative.id}`
            );
          }

          app.navigate(
            generatePath(APP_ROUTES.operatingEntities, {
              oeId,
              viewname: "initiatives",
            })
          );
          return;

        case "publish-confirmation":
          if (draftInitiativeToPublish) {
            app.patchAppState({
              editFormSaving: true,
              snackbar: {
                message: "All changes have been saved.",
                severity: "success",
                open: true,
                type: "snackbar",
              },
            });
            await saveInitiativeOrDraft({
              ...draftInitiativeToPublish,
              draft: false,
            });
            app.patchAppState({
              editFormSaving: false,
            });
          }

          return;

        case "delete-confirmation":
          if (currentEditInitiative) {
            resourceLockPlugin?.unlock(
              `${LOCK_RESOURCE_NAMES.initiative}-${currentEditInitiative.id}`
            );
          }
          void deleteInitiative();
          return;

        case "delete-document-confirmation":
          void deleteDocument(
            supportingDocumentIdToDelete,
            currentEditInitiative?.id?.toString()
          );
          break;

        case "navigate-with-changes":
          if (editFormNavigationPath) {
            app.navigate(editFormNavigationPath);
          }
          return;

        default:
          break;
      }
    });

    confirmationDialog.on("abort", () => {
      closeConfirmationDialog();
    });
  }
}
